import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';

import { authApi } from '../../services/api/auth.api';
import { LoginCredentials, Usuario } from '../../types/usuario';

export interface AuthState {
  user: Usuario | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoggedIn: boolean;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isLoggedIn: false,
  status: 'idle',
  error: null,
};

const extractMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    if (err.response?.status === 401) {
      return 'E-mail ou senha incorretos.';
    }
    if (err.response?.status === 400) {
      return 'Verifique os campos do formulário.';
    }
    if (!err.response) {
      return 'Não conseguimos conectar ao servidor. Tente novamente.';
    }
  }
  return 'Algo deu errado. Tente novamente em instantes.';
};

export const loginThunk = createAsyncThunk<
  { user: Usuario; accessToken: string; refreshToken?: string },
  LoginCredentials,
  { rejectValue: string }
>('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const response = await authApi.login(credentials);
    return response;
  } catch (err) {
    return rejectWithValue(extractMessage(err));
  }
});

export const fetchMeThunk = createAsyncThunk<Usuario, void, { rejectValue: void }>(
  'auth/me',
  async (_, { rejectWithValue }) => {
    try {
      return await authApi.me();
    } catch {
      return rejectWithValue();
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: () => initialState,
    clearError: (state) => {
      state.error = null;
      if (state.status === 'error') {
        state.status = 'idle';
      }
    },
    sessionExpired: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isLoggedIn = false;
      state.status = 'idle';
      state.error = 'Sua sessão expirou. Faça login novamente.';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken ?? null;
        state.isLoggedIn = true;
        state.status = 'idle';
        state.error = null;
      })
      .addCase(loginThunk.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.status = 'error';
        state.error = action.payload ?? 'Falha ao entrar.';
      })
      .addCase(fetchMeThunk.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isLoggedIn = true;
      })
      .addCase(fetchMeThunk.rejected, () => initialState);
  },
});

export const { logout, clearError, sessionExpired } = authSlice.actions;
export default authSlice.reducer;
